import { useCallback, useEffect, useRef, useState } from 'react';
import { ChatStreamService } from '../../services/chatStreamService';
import {
  fetchThreadHistory,
  mapHistoryMessages,
  mapTimeline,
  stubToWorkspaceArtifact,
} from '../../services/chatHistoryService';
import { fetchDashboardChatThread } from '../../services/dashboardChatService';
import { getTenantIdFromStorage } from '../../utils/onboardingLocalStorage';
import {
  decideSqlApproval,
  endTurn,
  processChatEvent,
  stopTurn,
  type ApprovalAction,
  type ChatStoreHook,
} from '../../chat/core';

/**
 * Chat docked beside a surface.
 *
 * A dashboard passes its `dashboardId` and the server decides which
 * conversation that is — per workspace, per board, per person. That is the only
 * thing a board has to do; it cannot accidentally get a browser-local chat, and
 * a board that opens as more than one page passes its `view` so each page keeps
 * its own conversation, as it keeps its own title and starters.
 *
 * A surface that is not a dashboard has no id to resolve, so it passes a
 * `threadKey` instead and its thread id is remembered in this browser. That id
 * lives on one machine: the same person on a second machine silently starts
 * again. Fine for a page whose chat is incidental, wrong for one people return
 * to — which is why a board never takes this route. `storageKey` overrides
 * where the id is kept, for a surface whose chats were saved under an older key.
 */
type SurfaceChatOptions = {
  useStore: ChatStoreHook;
  getSurfaceContext: () => Record<string, unknown> | null;
  enabled: boolean;
} & (
  | {
      /** The board this chat belongs to. The server owns the thread. */
      dashboardId: string;
      /** For a board that opens as more than one page. */
      view?: string;
      threadKey?: never;
      storageKey?: never;
    }
  | {
      /** Scopes a browser-remembered conversation, for a surface with no board behind it. */
      threadKey: string;
      storageKey?: string;
      dashboardId?: never;
      view?: never;
    }
);

export function useSurfaceChat(options: SurfaceChatOptions) {
  const { useStore, getSurfaceContext, enabled } = options;
  const { dashboardId, view, threadKey } = options;
  const storageKey = options.storageKey
    ?? (threadKey ? `klaris_surface_chat_thread:${threadKey}` : '');

  const getOrCreateThreadId = useCallback((): string => {
    try {
      const existing = window.localStorage.getItem(storageKey);
      if (existing) return existing;
      const id = crypto.randomUUID();
      window.localStorage.setItem(storageKey, id);
      return id;
    } catch {
      return crypto.randomUUID();
    }
  }, [storageKey]);

  const streamService = useRef(new ChatStreamService());
  const historyLoaded = useRef(false);
  // Held in state, not just the store, so loading history waits for it. A
  // server-resolved id arrives a round trip late, and an effect that read the
  // store once would decide there was no thread and never load anything.
  const [threadReady, setThreadReady] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (!dashboardId) {
      // A board whose id has not arrived from the route yet: wait for it rather
      // than start a conversation nothing can find again.
      if (!storageKey) return;
      const id = getOrCreateThreadId();
      useStore.getState().setThreadId(id);
      setThreadReady(id);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const id = await fetchDashboardChatThread(dashboardId, view);
        if (cancelled || !id) return;
        useStore.getState().setThreadId(id);
        setThreadReady(id);
      } catch (e) {
        console.warn('[useSurfaceChat] could not resolve the thread:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, dashboardId, view, storageKey, getOrCreateThreadId, useStore]);

  useEffect(() => {
    if (!enabled || historyLoaded.current) return;
    const threadId = threadReady ?? useStore.getState().threadId;
    if (!threadId) return;

    const tenantId = getTenantIdFromStorage();
    if (!tenantId) return;

    historyLoaded.current = true;
    void (async () => {
      try {
        useStore.getState().setIsLoadingHistory(true);
        const data = await fetchThreadHistory(tenantId, threadId);
        const store = useStore.getState();
        if (store.messages.length > 0) return;

        const artifacts = (data.artifacts || [])
          .filter((stub) => stub.type !== 'finding')
          .map(stubToWorkspaceArtifact);
        const findingRefs = new Set(
          (data.artifacts || []).filter((stub) => stub.type === 'finding').map((stub) => stub.ref),
        );

        store.loadHistoryShell({
          messages: mapHistoryMessages(data.messages || []),
          artifacts,
          timeline: mapTimeline(data.timeline || []).filter(
            (entry) => entry.type !== 'artifact' || !findingRefs.has(entry.artifactId),
          ),
        });

        if (data.thread_title) {
          store.setThreadTitle(data.thread_title);
        }
      } catch (e) {
        console.warn('[useSurfaceChat] history load failed:', e);
      } finally {
        useStore.getState().setIsLoadingHistory(false);
      }
    })();
  }, [enabled, threadReady]);

  const sendMessage = useCallback(
    async (message: string) => {
      const trimmed = message.trim();
      if (!trimmed) return;

      const token = window.localStorage.getItem('klaris_jwt');
      const tenantId = getTenantIdFromStorage();
      const threadId = useStore.getState().threadId
        ?? (dashboardId ? await fetchDashboardChatThread(dashboardId, view) : getOrCreateThreadId());
      if (!token || !tenantId) {
        useStore.getState().addMessage({
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: 'Error: Not authenticated',
          timestamp: new Date().toISOString(),
        });
        return;
      }

      const store = useStore.getState();
      store.setThreadId(threadId);
      const userMsgId = `user-${Date.now()}`;
      store.addMessage({
        id: userMsgId,
        role: 'user',
        content: trimmed,
        timestamp: new Date().toISOString(),
      });
      store.appendTimelineUser(userMsgId);
      store.setIsStreaming(true);
      store.resetStreaming();
      store.setStreamingAction('Thinking…');
      store.setInputText('');
      store.setPendingAuditId(null);

      const surfaceContext = getSurfaceContext();

      try {
        await streamService.current.sendMessage(
          trimmed,
          threadId,
          token,
          tenantId,
          (event) => processChatEvent(useStore.getState, event, threadId),
          undefined,
          'copilot',
          undefined,
          surfaceContext ?? undefined,
        );
      } catch (error: unknown) {
        const msg = error instanceof Error ? error.message : 'Unknown error occurred';
        const eid = `error-${Date.now()}`;
        store.addMessage({
          id: eid,
          role: 'assistant',
          content: `Error: ${msg}`,
          timestamp: new Date().toISOString(),
        });
        store.appendTimelineAssistantMessage(eid);
      } finally {
        endTurn(useStore.getState);
      }
    },
    [getSurfaceContext, dashboardId, view, getOrCreateThreadId],
  );

  /** The analyst's decision on a query the agent is waiting to run. */
  const sendSQLApproval = useCallback(
    async (action: ApprovalAction, editedSql?: string, reason?: string) => {
      const token = window.localStorage.getItem('klaris_jwt');
      const tenantId = getTenantIdFromStorage();
      const threadId = useStore.getState().threadId;
      if (!token || !tenantId || !threadId) return;

      const resumeData = decideSqlApproval(useStore.getState, action, editedSql, reason);
      if (!resumeData) return;

      try {
        await streamService.current.sendMessage(
          '',
          threadId,
          token,
          tenantId,
          (event) => processChatEvent(useStore.getState, event, threadId),
          resumeData,
          'copilot',
          undefined,
          // Resumed turns run with the surface's tools too, so send its context again.
          getSurfaceContext() ?? undefined,
        );
      } catch (error: unknown) {
        const msg = error instanceof Error ? error.message : 'Unknown error occurred';
        const eid = `error-${Date.now()}`;
        useStore.getState().addMessage({
          id: eid,
          role: 'assistant',
          content: `SQL approval error: ${msg}`,
          timestamp: new Date().toISOString(),
        });
        useStore.getState().appendTimelineAssistantMessage(eid);
      } finally {
        endTurn(useStore.getState);
      }
    },
    [getSurfaceContext],
  );

  const postContextNote = useCallback(
    (note: string) => {
      void sendMessage(note);
    },
    [sendMessage],
  );

  const stopStream = useCallback(() => {
    streamService.current.stop();
    stopTurn(useStore.getState);
  }, []);

  return { sendMessage, sendSQLApproval, postContextNote, stopStream };
}
